(() => {
  if (window.__WN_REVIEW_CAROUSEL_V9__) return;
  window.__WN_REVIEW_CAROUSEL_V9__ = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mod = (value, length) => ((value % length) + length) % length;

  class ReviewCarousel {
    constructor(root) {
      if (!root || root.dataset.reviewCarouselReady === 'true') return;
      this.root = root;
      this.track = root.querySelector('[data-review-track]');
      this.prev = root.querySelector('[data-review-prev]');
      this.next = root.querySelector('[data-review-next]');
      this.counter = root.querySelector('[data-review-counter]');
      this.progress = root.querySelector('[data-review-progress]');
      this.dots = [...root.querySelectorAll('[data-review-dots] > span')];
      this.allToggle = root.querySelector('[data-review-all-toggle]');
      this.allPanel = root.querySelector('[data-review-all]');
      this.writeButton = root.querySelector('[data-review-write]');
      this.writeClose = root.querySelector('[data-review-write-close]');
      this.judgeHost = root.querySelector('[data-review-judge-host]');
      this.autoplay = root.dataset.reviewCarousel === 'auto';
      this.loop = root.dataset.reviewLoop === 'true';
      this.realCards = [...root.querySelectorAll('[data-review-card]')]
        .sort((a,b)=>(Number(a.dataset.reviewOrder)||0)-(Number(b.dataset.reviewOrder)||0));
      this.cards = [];
      this.loopSize = 0;
      this.index = 0;
      this.pointer = null;
      this.timer = 0;
      this.resumeTimer = 0;
      this.frame = 0;
      this.loopSettleTimer = 0;
      this.visible = true;
      this.normalizing = false;

      if (!this.track || !this.realCards.length) return;
      this.loop ? this.buildLoop() : this.buildLinear();
      root.dataset.reviewCarouselReady = 'true';
      this.mount();
    }

    buildLinear() {
      const fragment = document.createDocumentFragment();
      this.realCards.forEach(card => fragment.appendChild(card));
      this.track.replaceChildren(fragment);
      this.cards = [...this.realCards];
      this.index = 0;
    }

    cloneCard(card, cycle) {
      const clone = card.cloneNode(true);
      clone.dataset.reviewClone = String(cycle);
      clone.setAttribute('aria-hidden','true');
      clone.tabIndex = -1;
      clone.querySelectorAll('button,a,input,textarea,select,[tabindex]').forEach(node => node.tabIndex = -1);
      return clone;
    }

    buildLoop() {
      this.loopSize = this.realCards.length;
      const fragment = document.createDocumentFragment();

      for (let cycle = 0; cycle < 5; cycle += 1) {
        if (cycle === 2) {
          this.realCards.forEach(card => fragment.appendChild(card));
        } else {
          this.realCards.forEach(card => fragment.appendChild(this.cloneCard(card,cycle)));
        }
      }

      this.track.replaceChildren(fragment);
      this.cards = [...this.track.querySelectorAll('[data-review-card]')];
      this.index = this.loopSize * 2;
    }

    mount() {
      this.track.addEventListener('dragstart', event => event.preventDefault());

      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.syncIndex());

        if (this.loop && !('onscrollend' in this.track)) {
          clearTimeout(this.loopSettleTimer);
          this.loopSettleTimer = window.setTimeout(() => this.normalizeLoop(), 360);
        }
      }, { passive:true });

      if ('onscrollend' in this.track && this.loop) {
        this.track.addEventListener('scrollend', () => this.normalizeLoop(), { passive:true });
      }

      this.track.addEventListener('pointerdown', event => this.pointerDown(event));
      this.track.addEventListener('pointermove', event => this.pointerMove(event));
      this.track.addEventListener('pointerup', event => this.pointerUp(event));
      this.track.addEventListener('pointercancel', () => this.pointerCancel());

      this.track.addEventListener('click', event => {
        if (performance.now() < (this.root.__wnSuppressReviewClickUntil || 0)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      this.prev?.addEventListener('click', () => { this.userPause(); this.go(this.index - 1); });
      this.next?.addEventListener('click', () => { this.userPause(); this.go(this.index + 1); });
      this.allToggle?.addEventListener('click', () => this.toggleAll());
      this.writeButton?.addEventListener('click', () => this.openWriteReview());
      this.writeClose?.addEventListener('click', () => this.closeWriteReview());

      this.root.addEventListener('mouseenter', () => this.stop());
      this.root.addEventListener('mouseleave', () => this.scheduleStart(700));
      this.root.addEventListener('focusin', () => this.stop());
      this.root.addEventListener('focusout', () => this.scheduleStart(900));
      this.root.addEventListener('touchstart', () => this.userPause(), { passive:true });
      document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.scheduleStart(800));

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting && entry.intersectionRatio >= .2;
          if (this.visible) this.scheduleStart(600); else this.stop();
        }, { threshold:[0,.2,.5] });
        this.observer.observe(this.root);
      }

      this.renderState();
      requestAnimationFrame(() => this.go(this.index,false));
      this.start();
    }

    cardCenter(card) {
      return card.offsetLeft + card.offsetWidth / 2;
    }

    targetFor(index) {
      const card = this.cards[index];
      if (!card) return 0;
      return Math.max(0,Math.min(
        this.track.scrollWidth - this.track.clientWidth,
        this.cardCenter(card) - this.track.clientWidth / 2
      ));
    }

    nearestIndex() {
      const viewportCenter = this.track.scrollLeft + this.track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      this.cards.forEach((card,index) => {
        const d = Math.abs(this.cardCenter(card) - viewportCenter);
        if (d < distance) { distance = d; closest = index; }
      });
      return closest;
    }

    syncIndex() {
      if (this.normalizing) return;
      const next = this.nearestIndex();
      if (next !== this.index) {
        this.index = next;
        this.renderState();
      }
    }

    logicalIndex() {
      if (!this.loop) return this.index;
      return mod(this.index,this.loopSize);
    }

    renderState() {
      const previous = this.index - 1;
      const next = this.index + 1;
      this.cards.forEach((card,index) => {
        card.classList.toggle('is-active',index===this.index);
        card.classList.toggle('is-prev',index===previous);
        card.classList.toggle('is-next',index===next);
      });

      const logical = this.logicalIndex();
      const total = this.loop ? this.loopSize : this.cards.length;
      if (this.counter) this.counter.textContent = `${logical + 1} / ${total}`;
      if (this.progress) this.progress.style.width = `${Math.max(7,((logical + 1) / total) * 100)}%`;
      this.dots.forEach((dot,index)=>dot.classList.toggle('is-active',index === (logical % this.dots.length)));
    }

    go(index,smooth=true) {
      if (!this.cards.length) return;
      if (this.loop) index = Math.max(0,Math.min(this.cards.length - 1,index));
      else index = mod(index,this.cards.length);

      this.index = index;
      this.track.scrollTo({
        left:this.targetFor(index),
        behavior:smooth && !reducedMotion.matches ? 'smooth' : 'auto'
      });
      this.renderState();
    }

    normalizeLoop() {
      if (!this.loop || this.normalizing || !this.loopSize) return;

      const centerStart = this.loopSize * 2;
      const centerEnd = this.loopSize * 3;
      let targetIndex = this.index;

      while (targetIndex < centerStart) targetIndex += this.loopSize;
      while (targetIndex >= centerEnd) targetIndex -= this.loopSize;
      if (targetIndex === this.index) return;

      this.normalizing = true;
      const oldBehavior = this.track.style.scrollBehavior;
      const oldSnap = this.track.style.scrollSnapType;
      this.track.style.scrollBehavior = 'auto';
      this.track.style.scrollSnapType = 'none';
      this.index = targetIndex;
      this.track.scrollLeft = this.targetFor(targetIndex);
      this.renderState();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.track.style.scrollBehavior = oldBehavior;
          this.track.style.scrollSnapType = oldSnap;
          this.normalizing = false;
        });
      });
    }

    pointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('button,a,input,textarea,select,label')) return;
      this.pointer={id:event.pointerId,x:event.clientX,y:event.clientY,scroll:this.track.scrollLeft,moved:false,captured:false};
      this.userPause();
    }

    pointerMove(event) {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const dx=event.clientX-this.pointer.x;
      const dy=event.clientY-this.pointer.y;
      if (!this.pointer.moved) {
        if (Math.abs(dx)<6 || Math.abs(dx)<=Math.abs(dy)) return;
        this.pointer.moved=true;
        try { this.track.setPointerCapture?.(event.pointerId); this.pointer.captured=true; } catch(_){}
        this.track.classList.add('is-dragging');
        window.getSelection?.()?.removeAllRanges();
      }
      event.preventDefault();
      this.track.scrollLeft=this.pointer.scroll-dx;
    }

    pointerUp(event) {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const moved=this.pointer.moved;
      this.pointerCancel();
      if (moved) {
        this.root.__wnSuppressReviewClickUntil=performance.now()+320;
        this.go(this.nearestIndex(),true);
      } else {
        this.syncIndex();
      }
    }

    pointerCancel() {
      if (this.pointer?.captured) {
        try { this.track.releasePointerCapture?.(this.pointer.id); } catch(_){}
      }
      this.pointer=null;
      this.track?.classList.remove('is-dragging');
    }

    toggleAll() {
      if (!this.allToggle || !this.allPanel) return;
      const opening=this.allPanel.hidden;
      this.allPanel.hidden=!opening;
      this.allToggle.setAttribute('aria-expanded',opening?'true':'false');
      const label=this.allToggle.querySelector('[data-review-all-label]');
      if (label) label.textContent=opening?this.allToggle.dataset.labelClose:this.allToggle.dataset.labelOpen;
      const arrow=this.allToggle.querySelector('.wn-review-action__arrow');
      if (arrow) arrow.textContent=opening?'↑':'→';
      if (opening) window.setTimeout(()=>this.allPanel.scrollIntoView({behavior:reducedMotion.matches?'auto':'smooth',block:'nearest'}),50);
    }

    openWriteReview() {
      if (!this.judgeHost) return;
      this.judgeHost.hidden=false;
      this.judgeHost.setAttribute('aria-hidden','false');
      this.root.classList.add('is-writing');
      this.judgeHost.scrollIntoView({behavior:reducedMotion.matches?'auto':'smooth',block:'center'});
      this.triggerNativeWrite(0);
    }

    triggerNativeWrite(attempt) {
      if (!this.judgeHost || this.judgeHost.hidden) return;
      const form=this.judgeHost.querySelector('.jdgm-form-wrapper, .jdgm-form');
      if (form && form.getBoundingClientRect().height>12) {
        form.scrollIntoView({behavior:reducedMotion.matches?'auto':'smooth',block:'center'});
        form.querySelector('input,textarea,select,button')?.focus({preventScroll:true});
        return;
      }
      const trigger=this.judgeHost.querySelector('.jdgm-write-rev-link,[data-write-review],[data-judgeme-write-review]');
      if (trigger && !trigger.dataset.wnTriggered) { trigger.dataset.wnTriggered='true'; trigger.click(); }
      if (attempt<40) window.setTimeout(()=>this.triggerNativeWrite(attempt+1),180);
    }

    closeWriteReview() {
      if (!this.judgeHost) return;
      this.judgeHost.hidden=true;
      this.judgeHost.setAttribute('aria-hidden','true');
      this.root.classList.remove('is-writing');
      this.writeButton?.focus({preventScroll:true});
    }

    start() {
      if (!this.autoplay || reducedMotion.matches || document.hidden || !this.visible || this.cards.length<2 || this.timer) return;
      this.timer=window.setTimeout(()=>{
        this.timer=0;
        if(this.autoplay&&this.visible&&!document.hidden){
          this.go(this.index+1,true);
          this.start();
        }
      },5000);
    }

    stop() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer=0;
      if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
      this.resumeTimer=0;
    }

    scheduleStart(delay=0) {
      if (!this.autoplay) return;
      if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
      this.resumeTimer=window.setTimeout(()=>{this.resumeTimer=0;this.start();},delay);
    }

    userPause(){this.stop();this.scheduleStart(5200);}
  }

  const boot=(scope=document)=>scope.querySelectorAll?.('[data-review-carousel]').forEach(root=>new ReviewCarousel(root));
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',()=>boot(),{once:true}); else boot();
  document.addEventListener('shopify:section:load',event=>boot(event.target));
})();